import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PersonAdd, Email, Phone, ArrowForward } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { signUpThunk } from '../features/auth/authSlice';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    padding: theme.spacing(1),
    minWidth: 400,
    [theme.breakpoints.down('sm')]: {
      minWidth: '90vw',
      margin: theme.spacing(2),
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const UserNotFoundDialog = ({ open, onClose, identifier, onSuccess }) => {
  const [signupData, setSignupData] = useState({
    name: '',
    email: identifier?.includes('@') ? identifier : '',
    phone: identifier?.includes('@') ? '' : identifier,
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const dispatch = useDispatch();
  const theme = useTheme();

  const handleInputChange = (field, value) => {
    setSignupData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!signupData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!signupData.email && !signupData.phone) {
      newErrors.email = 'Email or phone number is required';
      newErrors.phone = 'Email or phone number is required';
    }
    
    if (signupData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (signupData.phone && !/^\+[1-9]\d{1,14}$/.test(signupData.phone)) {
      newErrors.phone = 'Please enter a valid phone number in E.164 format (e.g., +911234567890)';
    }
    
    if (!signupData.password) {
      newErrors.password = 'Password is required';
    } else if (signupData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }
    
    if (signupData.password !== signupData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Ensure we have exactly one contact method
    if (signupData.email && signupData.phone) {
      newErrors.email = 'Please provide either email or phone, not both';
      newErrors.phone = 'Please provide either email or phone, not both';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const signupPayload = {
        name: signupData.name,
        password: signupData.password,
        password2: signupData.confirmPassword,
        role: 'patient',
        contact: signupData.email || signupData.phone, // Use the contact field as expected by backend
      };
      
      console.log('Signup payload:', signupPayload); // Debug log
      
      const result = await dispatch(signUpThunk(signupPayload)).unwrap();
      
      toast.success('Account created successfully! Welcome to MindWell! 🎉', {
        icon: '🌟'
      });
      
      onSuccess(result);
      onClose();
    } catch (error) {
      const errorMessage = error?.non_field_errors?.[0] || 
                          error?.contact?.[0] || 
                          error?.email?.[0] || 
                          error?.phone_number?.[0] || 
                          error?.password?.[0] ||
                          'Failed to create account. Please try again.';
      
      toast.error(errorMessage, { icon: '💭' });
    } finally {
      setLoading(false);
    }
  };

  const handleTryDifferentIdentifier = () => {
    onClose();
    // This will take user back to login form
  };

  const isEmail = identifier?.includes('@');

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 700
      }}>
        <PersonAdd sx={{ fontSize: 48, mb: 2, color: theme.palette.primary.main }} />
        <Typography variant="h5" component="div">
          Create Your Account
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            We couldn't find an account with <strong>{identifier}</strong>. 
            Let's create one for you! 🚀
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Full Name"
            value={signupData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 2 }}
          />
          
          {isEmail ? (
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={signupData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ mb: 2 }}
            />
          ) : (
            <TextField
              fullWidth
              label="Phone Number"
              value={signupData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone}
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ mb: 2 }}
            />
          )}
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={signupData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={signupData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <StyledButton
          onClick={handleTryDifferentIdentifier}
          variant="outlined"
          disabled={loading}
        >
          Try Different Email/Phone
        </StyledButton>
        
        <StyledButton
          onClick={handleSignup}
          variant="contained"
          disabled={loading}
          endIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default UserNotFoundDialog;
